import { ref, computed } from "vue";
import {
  SfActivityMode,
  SfActivityScreen,
  SfActivityWord,
  SfActivtyAttempt,
} from "@/types/sfActivityTypes";
import SfActivityAudio from "@/func/game/SfActivityAudio";
import buildActivityWords from "@/func/game/buildActivityWords";
import { SfSpellingTestAnswerWord, SfWordElement } from "@/types/spellingTypes";
import sfPointsCaclulator from "@/func/game/sfPointsCaclulator";
import { spellingSaveGameData } from "./spellingGameState";

let testTime = 0;
let timer = null as number | null;

const startTimer = () => {
  testTime = 0;
  timer = setInterval(() => {
    testTime++;
  }, 1000);
};

const sfActivityWords = ref(null as SfActivityWord[] | null);
const sfActivityAudio = ref(null as any);
const sfWordResults = ref(null as null | SfSpellingTestAnswerWord[]);

const sfActivityState = ref({
  mode: "test" as SfActivityMode,
  testResultId: 0,
  readSentences: false,
  screen: "intro" as SfActivityScreen,
  loadProgress: 0,
  currentWordId: -1,
  currentSentenceId: 0,
  testTime: 0,
  gameCompleted: false,
});

export const sfActivityMode = computed(() => sfActivityState.value.mode);

export const sfActivityLoadProgress = computed(
  () => sfActivityState.value.loadProgress,
);

export const sfCurrentActivityScreen = computed(
  () => sfActivityState.value.screen,
);

export const sfActivityWordCount = computed(() =>
  sfActivityWords.value ? sfActivityWords.value.length : 0,
);

export const sfActivityCurrentWordId = computed(
  () => sfActivityState.value.currentWordId,
);

export const sfActivityCurrentWord = computed(() =>
  sfActivityWords.value
    ? sfActivityWords.value[sfActivityState.value.currentWordId]
    : null,
);

export const sfActivityCurrentSentence = computed((): SfWordElement | null => {
  if (
    !sfActivityCurrentWord.value ||
    sfActivityCurrentWord.value.sentences.length < 1
  ) {
    return null;
  }
  return sfActivityCurrentWord.value.sentences[
    sfActivityState.value.currentSentenceId
  ];
});

export const sfActivityCompleted = computed(
  () => sfActivityCurrentWordId.value >= sfActivityWordCount.value,
);

export const sfActivityAttemptCount = computed(() => {
  const currentId = sfActivityCurrentWordId.value;
  const current = sfWordResults.value?.find((r) => r.wordId == currentId);
  return current ? current.attempts.length : 0;
});

export const sfACtivityPoints = computed(() =>
  sfPointsCaclulator(sfWordResults.value, sfActivityMode.value),
);

export const sfCreateActivity = () => {
  sfWordResults.value = [];
  sfActivityWords.value = null;
  sfActivityState.value = {
    ...sfActivityState.value,
    loadProgress: -1,
    testTime: 0,
    screen: "intro" as SfActivityScreen,
  };
};

export const sfDestroyActivity = () => {
  if (sfActivityAudio.value) {
    sfActivityAudio.value.destroy();
  }
  sfActivityAudio.value = null;
  sfActivityWords.value = null;
  sfWordResults.value = null;
  sfActivityState.value = {
    ...sfActivityState.value,
    screen: "intro" as SfActivityScreen,
  };
  if (timer) {
    clearInterval(timer);
  }
};

export const sfInitActivity = async (mode: SfActivityMode) => {
  sfActivityAudio.value = SfActivityAudio();
  sfActivityState.value = {
    ...sfActivityState.value,
    mode: mode,
    loadProgress: 0,
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  sfActivityAudio.value.onProgress = (progress: number) => {
    sfActivityState.value.loadProgress = progress;
  };
  const loadedWords = await (window as any).sfStartGame(mode);
  if (!loadedWords) {
    throw "error loading words";
  }
  const words = buildActivityWords(loadedWords.words);
  sfActivityWords.value = words;
  sfActivityAudio.value.loadWordsAudio(words);
};

export const sfPlayActivityAudio = (
  id: string,
  next = null as string | null,
) => {
  if (!sfActivityAudio.value) {
    return;
  }
  sfActivityAudio.value.clearQueue();
  if (next != null) {
    sfActivityAudio.value.addToQueue(next);
  }
  sfActivityAudio.value.play(id);
};

export const sfStopActivityAudio = () => {
  if (!sfActivityAudio.value) {
    return;
  }
  sfActivityAudio.value.stop();
};

export const sfStartActivity = (readSentences: boolean) => {
  sfActivityState.value = {
    ...sfActivityState.value,
    readSentences: readSentences,
    gameCompleted: false,
    screen: "game",
    currentWordId: -1,
  };
  sfActivityNextWord();
  startTimer();
};

export const sfActivityNextWord = () => {
  sfActivityState.value.currentWordId = sfActivityState.value.currentWordId + 1;
  if (sfActivityCompleted.value) {
    sfActivityFinished();
    return;
  }
  let currentSentenceId = 0;
  if (
    sfActivityCurrentWord.value &&
    sfActivityCurrentWord.value.sentences.length > 0
  ) {
    currentSentenceId = Math.floor(
      Math.random() * sfActivityCurrentWord.value.sentences.length * 0.9999,
    );
  }
  sfActivityState.value.currentSentenceId = currentSentenceId;
  const wordAudio = sfActivityCurrentWord.value
    ? "w_" + sfActivityCurrentWord.value.id
    : null;
  if (sfActivityState.value.readSentences && sfActivityCurrentSentence.value) {
    sfPlayActivityAudio("sen_" + sfActivityCurrentSentence.value.id, wordAudio);
  } else if (wordAudio) {
    sfPlayActivityAudio(wordAudio);
  }
};

/**
 *
 * @param data
 * @returns Attempt count
 */
export const sfActivityNewAttempt = (data: SfActivtyAttempt) => {
  const results = sfWordResults.value ? [...sfWordResults.value] : [];
  let wordResult = null;
  let i = 0 as any;
  for (i in results) {
    if (results[i].wordId == data.wordId) {
      wordResult = { ...results[i] };
      break;
    }
  }
  if (!wordResult) {
    wordResult = { wordId: data.wordId, attempts: [] };
    i = results.length;
  }
  data.attempt.test_time = testTime;
  const attempts = [...wordResult.attempts, data.attempt];
  wordResult.attempts = attempts;
  results[i] = wordResult;
  sfWordResults.value = results;
  return attempts.length;
};

const sfActivityFinished = () => {
  if (sfWordResults.value) {
    spellingSaveGameData(sfWordResults.value);
  }
};
